import type { UserListItem } from "@/modules/dashboard/users/types/users-api.types";

import type { AssignedRole, EffectiveSummary } from "../../types/access.types";

/**
 * Everything the users list needs to paint one page, shared by the table and the cards so the two faces of
 * the same data cannot drift apart. `AccessUsersTableProps` extends this rather than declaring its own — the
 * same arrangement `access/components/ApiKeysCards.tsx` uses.
 */
export interface AccessUsersListProps {
  /** The loaded page, already filtered and paginated by the backend. */
  users: UserListItem[];
  /** Assigned roles per user id. A missing entry is "none", not "unknown" — see `rolesUnavailable`. */
  rolesByUser: Record<string, AssignedRole[]>;
  /** User ids whose roles fetch FAILED. Their access is unknown, never confirmed as none. */
  rolesUnavailable: ReadonlySet<string>;
  /** Effective access per user id, resolved deny-first against the roles catalog. */
  summaryById: Record<string, EffectiveSummary>;
  /** The roles catalog 403'd: effective access cannot be resolved and role management is off. */
  catalogUnavailable: boolean;
  /** Whether the viewer may edit roles — decides the action's icon and wording, never its presence. */
  canManage: boolean;
  /** Id of the signed-in user, to mark their own row. */
  currentUserId?: string;
  /** The list came back empty because the request failed, not because nobody matches. */
  loadFailed: boolean;
  /** Opens the assign-roles drawer for a user. Callers gate it per row — see `canOpenRolesFor`. */
  onOpenRoles: (user: UserListItem) => void;
}

/**
 * Whether the assign-roles drawer can be offered for this row.
 *
 * Both halves matter and neither is cosmetic: the drawer resolves its role list and its live matrix against
 * the catalog, so without it the drawer would offer "no roles available"; and opening it for a principal
 * whose roles are UNKNOWN would render every box clear, which is indistinguishable from a confirmed "holds
 * nothing". Shared so the table and the cards cannot answer this differently.
 */
export function canOpenRolesFor(
  userId: string,
  catalogUnavailable: boolean,
  rolesUnavailable: ReadonlySet<string>,
): boolean {
  return !catalogUnavailable && !rolesUnavailable.has(userId);
}
